import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { temperatureContext } from '../../../lib/context';
import { CONSTANTS } from '../composables/utils';
import DeviceDetailsModal from './DeviceDetailsModal';

function DeviceCard({ record, isArchived }: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'RealTime' });
    const tempUnit = useContext(temperatureContext);
    const [openDetail, setOpenDetail] = useState(false);
    const [showToast, setShowToast] = useState(false);

    const color = record.normal ? CONSTANTS.COLORS.CARD_NORMAL : CONSTANTS.COLORS.CARD_ABNORMAL;
    const unitLabel = tempUnit === 'fahrenheit' ? '°F' : '°C';

    const hasHumidity = record.hasHumidity;
    const humidityColor = record.humidityNormal ? CONSTANTS.COLORS.CARD_NORMAL : CONSTANTS.COLORS.CARD_ABNORMAL;

    const renderArchivedBody = () => (
        <div className='flex aspect-square w-full max-w-[150px] flex-col items-center justify-center overflow-hidden rounded-full bg-[#F1F1F1] shadow-[inset_0_0_5px_rgba(0,0,0,0.25)]'>
            <div className='flex w-full items-center justify-center gap-2 px-5 text-xs font-semibold text-[#737373]'>
                <span className='h-px flex-1 bg-[#b5b5b5]'></span>
                <span>
                    {record.activeUpperLimit}
                    {unitLabel}
                </span>
                <span className='h-px flex-1 bg-[#b5b5b5]'></span>
            </div>
            <div className='my-4 flex items-center justify-center'>
                <img src='/archived.svg' alt='Archived' className='h-[62.5px] w-[62.5px]' />
            </div>
            <div className='flex w-full items-center justify-center gap-2 px-5 text-xs font-semibold text-[#737373]'>
                <span className='h-px flex-1 bg-[#b5b5b5]'></span>
                <span>
                    {record.activeLowerLimit}
                    {unitLabel}
                </span>
                <span className='h-px flex-1 bg-[#b5b5b5]'></span>
            </div>
        </div>
    );

    const renderDisconnectedBody = () => (
        <div
            className={
                hasHumidity
                    ? 'flex h-[125px] w-full items-center justify-center gap-2 px-1 md:h-[150px] md:gap-3 md:px-2'
                    : 'flex aspect-square w-full max-w-[150px] items-stretch justify-center'
            }
        >
            <DisconnectedDial
                upper={record.activeUpperLimit}
                lower={record.activeLowerLimit}
                unit={unitLabel}
                paired={hasHumidity}
            />
            {hasHumidity && (
                <DisconnectedDial
                    upper={record.activeHumidityUpperLimit}
                    lower={record.activeHumidityLowerLimit}
                    unit='%'
                    paired
                />
            )}
        </div>
    );

    return (
        <div
            role='button'
            tabIndex={0}
            aria-label={record.name}
            className='flex h-full w-full cursor-pointer flex-col items-center gap-3 rounded-[28px] bg-white p-4 text-slate-600 transition-all'
            onClick={() => {
                setOpenDetail(true);
            }}
            onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setOpenDetail(true);
                }
            }}
        >
            <div className='w-full truncate text-center text-sm font-semibold'>{record.name}</div>

            {isArchived ? (
                renderArchivedBody()
            ) : record.connected ? (
                <div
                    className={
                        hasHumidity
                            ? 'flex h-[125px] w-full items-center justify-center gap-2 px-1 md:h-[150px] md:gap-3 md:px-2'
                            : 'flex aspect-square w-full max-w-[150px] items-stretch justify-center'
                    }
                >
                    <Gauge
                        upper={record.activeUpperLimit}
                        lower={record.activeLowerLimit}
                        value={record.lastItem.temperature}
                        unit={unitLabel}
                        color={color}
                        testId='card-temperature'
                        paired={hasHumidity}
                    />
                    {hasHumidity && (
                        <Gauge
                            upper={record.activeHumidityUpperLimit}
                            lower={record.activeHumidityLowerLimit}
                            value={record.humidityReading ?? '--'}
                            unit='%'
                            color={humidityColor}
                            testId='card-humidity'
                            paired
                        />
                    )}
                </div>
            ) : (
                renderDisconnectedBody()
            )}
            <div className='mt-3 flex w-full justify-center text-xs text-slate-500'>
                <span className='truncate text-[12px]'>
                    {t('deviceID')}: {record.deviceId}
                </span>
            </div>
            {openDetail && (
                <DeviceDetailsModal
                    record={record}
                    open={openDetail}
                    onClose={() => setOpenDetail(false)}
                    isArchived={isArchived}
                />
            )}
        </div>
    );
}

// One reading dial. Alone it is the circle the card has always shown; paired it
// narrows to an ellipse so two fit the card's existing footprint — issue #206 asks
// for humidity to be fitted into the current square, not for the card to grow.
function Gauge({ upper, lower, value, unit, color, testId, paired = false }: any) {
    return (
        <div
            className={`flex flex-col items-center justify-center rounded-full border border-slate-200 bg-white ${
                // Paired dials stay perfectly round — they shrink to share the width
                // rather than compressing into ellipses. Capped at the row height so a
                // wide card can't push them past it.
                paired ? 'h-full min-w-0 flex-1' : 'h-full w-full'
            }`}
            style={{ backgroundImage: `linear-gradient(${color}33 50%, ${color} 50%)` }}
        >
            <Limit value={upper} unit={unit} paired={paired} />
            <div
                className={
                    paired
                        ? 'my-0.5 flex items-baseline justify-center leading-none'
                        : 'my-3 flex items-baseline justify-center'
                }
            >
                <span
                    data-testid={testId}
                    className={`font-light text-slate-600 ${paired ? 'text-xs md:text-3xl' : 'text-4xl'}`}
                >
                    {value}
                </span>
                <span className={`ml-0.5 font-semibold text-slate-600 ${paired ? 'text-[7px] md:text-xs' : 'text-sm'}`}>
                    {unit}
                </span>
            </div>
            <Limit value={lower} unit={unit} paired={paired} />
        </div>
    );
}

function DisconnectedDial({ upper, lower, unit, paired = false }: any) {
    return (
        <div
            className={`flex flex-col items-center justify-center overflow-hidden rounded-full bg-[#F1F1F1] shadow-[inset_0_0_5px_rgba(0,0,0,0.25)] ${
                paired ? 'h-full min-w-0 flex-1' : 'aspect-square w-full max-w-[150px]'
            }`}
        >
            <DisconnectedLimit value={upper} unit={unit} paired={paired} />
            <div className={paired ? 'my-1 flex items-center justify-center' : 'my-4 flex items-center justify-center'}>
                <img src='/offline.svg' alt='Offline' className={paired ? 'h-6 w-6 md:h-8 md:w-8' : 'h-10 w-10'} />
            </div>
            <DisconnectedLimit value={lower} unit={unit} paired={paired} />
        </div>
    );
}

function DisconnectedLimit({ value, unit, paired }: any) {
    return (
        <div
            className={`flex w-full items-center justify-center font-semibold text-[#737373] ${
                paired
                    ? 'gap-0.5 px-1 text-[7px] leading-none md:gap-1 md:px-4 md:text-xs md:leading-tight'
                    : 'gap-2 px-5 text-xs'
            }`}
        >
            <span className='h-px flex-1 bg-[#b5b5b5]'></span>
            <span className='whitespace-nowrap'>
                {value}
                {unit}
            </span>
            <span className='h-px flex-1 bg-[#b5b5b5]'></span>
        </div>
    );
}

// The hairline-flanked limit label above and below a gauge's reading.
function Limit({ value, unit, paired }: any) {
    return (
        <div
            className={`flex w-full items-center justify-center text-slate-500 ${
                paired
                    ? 'gap-0.5 px-1 text-[7px] leading-none md:gap-1 md:px-4 md:text-xs md:leading-tight'
                    : 'gap-2 px-5 text-xs'
            }`}
        >
            <span className='h-px flex-1 bg-slate-300'></span>
            <span className='whitespace-nowrap'>
                {value}
                {unit}
            </span>
            <span className='h-px flex-1 bg-slate-300'></span>
        </div>
    );
}

export default DeviceCard;
